import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useContext,
  useState,
} from "react";
import {
  AdyenCheckout,
  Dropin,
  Card,
  ApplePay,
  GooglePay,
  Klarna,
  BacsDirectDebit,
  WeChat,
  PayPal,
} from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import "./adyen-overrides.css";
import { useNavigate } from "react-router-dom";
import {
  createThirdPartyPayment,
  initThirdPartyPayment,
  finalizeThirdPartyPayment,
} from "@api";
import { navigateOnPaymentCompletion } from "./adyen/shared";

const AdyenPaymentBnpl = ({
  paymentParameters, // { amount, currency, reference, returnUrl, … }
  processingUrl,
  externalSubmitRef,
  onAmountChange,
  onError,
  setAdyenLoading,
  setProcessing,
  klarnaEmailAddress,
  klarnaEmailAddressConfirmed,
}) => {
  const navigate = useNavigate();
  const dropinRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const paymentDataRef = useRef(null);
  const hasAutoSubmittedRef = useRef(false);

  const triggerSubmitPayment = () => {
    // Try to simulate a real user click on Adyen's own pay button
    const payButton = document.querySelector(
      ".adyen-bnpl-wrapper .adyen-checkout__button.adyen-checkout__button--pay"
    );

    if (payButton) {
      console.log("Clicking Adyen pay button DOM element");
      setErrorMessage(null);
      payButton.click();
      return;
    }

    // Fallback: if for some reason the button isn't found, use the Drop-in API
    if (dropinRef.current) {
      console.warn(
        "Adyen pay button DOM element not found; falling back to dropin.submit()"
      );
      setErrorMessage(null);
      dropinRef.current.submit();
    } else {
      console.warn("Drop-in is not mounted yet");
    }
  };

  useEffect(() => {
    if (!paymentParameters) {
      return;
    }

    let mounted = true;

    async function init() {
      if (!mounted) return;

      // setProcessing?.(true);
      setAdyenLoading?.(true);

      if (dropinRef.current) {
        dropinRef.current.unmount();
        dropinRef.current = null;
      }

      const initPaymentResponse = await initThirdPartyPayment({
        merchantId: paymentParameters.merchantId,
        amount: paymentParameters.amount,
        paymentType: "KlarnaPayOverTime",
        paymentProvider: "Adyen",
        customerIsFinanced: paymentParameters.customerIsFinanced ?? false,
      });

      // Notify parent of amount (if provided by API)
      if (initPaymentResponse.amount) {
        onAmountChange?.(initPaymentResponse.amount);
      }

      const { paymentMethods, clientKey, environment } = JSON.parse(
        initPaymentResponse.clientData
      );
      const adyenPaymentMethodsData = JSON.parse(paymentMethods);

      const globalConfiguration = {
        paymentMethodsResponse: adyenPaymentMethodsData,
        // Keep the internal pay button rendered so we can safely "click" it
        // programmatically from the parent. It is visually hidden via CSS.
        showPayButton: true,
        environment: environment.toLowerCase(),
        clientKey: clientKey,
        onSubmit: async (state, component, actions) => {
          try {
            const createPaymentResponse = await createThirdPartyPayment({
              ...paymentParameters,
              paymentProvider: "Adyen",
              paymentType: "KlarnaPayOverTime",
              customerEmail: klarnaEmailAddress,
              clientData: JSON.stringify(state.data),
            });

            paymentDataRef.current = createPaymentResponse;

            if (createPaymentResponse.tokenExpired) {
              onError?.({ tokenExpired: true });
              return;
            }

            if (createPaymentResponse.clientData) {
              const parsedClientData = JSON.parse(
                createPaymentResponse.clientData
              );

              actions.resolve({
                resultCode: parsedClientData.resultCode,
                action: parsedClientData.action,
                order: parsedClientData.order,
                donationToken: parsedClientData.donationToken,
              });
            }
          } catch (err) {
            console.error("onSubmit", error);
            actions.reject();
          }
        },

        onAdditionalDetails: async (state, component, actions) => {
          try {
            const finalizePaymentResponse = await finalizeThirdPartyPayment({
              merchantId: paymentParameters.merchantId,
              paymentId: paymentDataRef.current.id,
              clientData: JSON.stringify(state.data),
              paymentProvider: "Adyen",
            });

            const result = JSON.parse(finalizePaymentResponse.clientData);

            // If the /payments/details request from your server fails, or if an unexpected error occurs.
            if (!result.resultCode) {
              actions.reject();
              return;
            }

            const { resultCode, action, order, donationToken } = result;

            actions.resolve({
              resultCode,
              action,
              order,
              donationToken,
            });
          } catch (error) {
            console.error("onSubmit", error);
            actions.reject();
          }
        },
        onPaymentCompleted: (result, component) => {
          console.log("Payment completed:", result);

          // https://docs.adyen.com/online-payments/build-your-integration/payment-result-codes/
          if (
            result.resultCode === "Authorised" ||
            result.resultCode === "Pending" ||
            result.resultCode === "Received"
          ) {
            navigateOnPaymentCompletion({
              navigate,
              baseUrl: processingUrl,
              paymentId: paymentDataRef.current?.id,
              fallbackPath: "/",
              paymentType: "thirdparty",
            });
          } else {
            // This path should never be reached, because declined payments trigger onPaymentFailed rather than onPaymentCompleted
            setErrorMessage("Your payment was declined. Please try again.");
          }
        },
        onChange: (state, dropin) => {
          if (!state.isValid) {
            setProcessing?.(false);
          }
        },
        onError: (error) => {
          console.error(error);
          setProcessing?.(false);
          if (error?.finalize_required === false) return;
          setErrorMessage("An unknown error occurred. Please try again later.");
        },
        onPaymentFailed: (result) => {
          console.warn("Payment failed", result);
          setErrorMessage(
            "Payment failed. Please check your details and try again or use a different payment method."
          );
          setProcessing?.(false);
          dropinRef.current.setStatus("ready");
        },
        locale: "en-US",
        countryCode: "GB",
        translations: {
          "en-US": {
            zipCode: "Postal code",
          },
        },
      };

      const checkout = await AdyenCheckout(globalConfiguration);
      const dropinConfiguration = {
        paymentMethodComponents: [Klarna],
        // Optional configuration.
        disableFinalAnimation: true,
        showRadioButton: false,
        // Explicitly remove all non-Klarna methods to avoid card or wallets showing
        removePaymentMethods: [
          "scheme", // card
          "applepay",
          "googlepay",
          "paywithgoogle",
          "paypal",
          "wechatpay",
          "bacs",
          "alipay",
          "ideal",
          "afterpay",
          "klarna_payover4", // example of other BNPL flavors you might not want
        ],
        onReady: () => {
          // Hide skeleton once drop-in fully renders
          setAdyenLoading?.(false);

          // Only auto-submit once per mount, and only when the Klarna email
          // has been confirmed by the parent.
          if (hasAutoSubmittedRef.current) {
            return;
          }
          hasAutoSubmittedRef.current = true;

          setTimeout(() => {
            triggerSubmitPayment();
          }, 100);
        },
        onError: (error) => {
          setProcessing?.(false);
        },
        paymentMethodsConfiguration: {
          klarna: {
            useKlarnaWidget: true, // When set to true, the Klarna widget is shown. Set to false to initiate a redirect flow.
          },
          klarna_account: {
            useKlarnaWidget: true, // When set to true, the Klarna widget is shown. Set to false to initiate a redirect flow.
          },
          klarna_paynow: {
            useKlarnaWidget: true, // When set to true, the Klarna widget is shown. Set to false to initiate a redirect flow.
          },
        },
      };

      const dropin = new Dropin(checkout, dropinConfiguration).mount(
        "#adyen-klarna-dropin-container"
      );

      dropinRef.current = dropin;
    }

    init();

    return () => {
      mounted = false;
      dropinRef.current?.unmount();
    };
  }, [
    paymentParameters,
    processingUrl,
    setAdyenLoading,
    setProcessing,
    klarnaEmailAddress,
    klarnaEmailAddressConfirmed,
  ]);

  // expose programmatic submit if parent wants it
  useImperativeHandle(externalSubmitRef, () => ({
    submitPayment: () => {
      triggerSubmitPayment();
    },
  }));

  return (
    <div className="adyen-bnpl-wrapper">
      {errorMessage && (
        <div className="text-red-600 text-sm rounded-3 p-2 mb-4" role="alert">
          {errorMessage}
        </div>
      )}
      <div id="adyen-klarna-dropin-container" />
    </div>
  );
};

export { AdyenPaymentBnpl };
